import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import type { Signal, Repeat } from '@bayer-int/portletce-shared';
import {
    Validation,
    VegaSpec,
    PipeDocument,
    SpecType,
    SignalReference,
} from '@bayer-int/portletce-shared';
import { isUndefined, omitBy } from 'lodash-es';
import type { Specification, DocApplyOptions } from '@bayer-int/portletce-aclibv2';

import type { HttpService } from 'portlet/common/services/HttpService';
import type { Boot } from 'portlet/config/boot';
import { resolveBootPlaceholders } from 'portlet/common/utils/placeholders';
import { isString, concatUniqByProp } from 'portlet/common/utils';
import { createValidationError } from 'portlet/common/errors';

import type { AcSpecOrId, AcSpecification, AcPipeDocument, AcPipeDocOrId } from '../models';
import { View } from '../models';

export class VegaSpecificationService {
    private vegaSpecUrl = this.boot.servers.vegaSpec.url;
    private static mergeSignals = concatUniqByProp('name');

    constructor(
        private httpService: HttpService,
        private boot: Boot,
    ) {}

    public getSpecDocument(spec: AcPipeDocOrId): Observable<AcPipeDocument> {
        if (!isString(spec) && !View.docIdWithScopedProperties.is(spec)) return of(spec);
        if (!isString(spec) && spec.spec) return of(spec.spec);

        const id: string = isString(spec) ? spec : spec.name;
        const version: number | undefined = isString(spec) ? undefined : spec.version;
        const specVersion = version ? `/version/${version}` : '';
        const url = `${this.vegaSpecUrl}${id}${specVersion}?type=vegaspec`;

        return this.httpService.get(url).pipe(
            map(({ data }) => data),
            map(Validation.create.toThrow(PipeDocument.codec, createValidationError)),
            map(spec => resolveBootPlaceholders(spec, this.boot)),
        );
    }

    public getVegaSpec(spec: AcSpecOrId): Observable<AcSpecification> {
        if (!isString(spec) && !View.specIdWithScopedProperties.is(spec)) return of(spec);
        if (!isString(spec) && spec.spec) return of(spec.spec);

        const id: string = isString(spec) ? spec : spec.name;
        const version: number | undefined = isString(spec) ? undefined : spec.version;
        const specVersion = version ? `/version/${version}` : '';
        const url = `${this.vegaSpecUrl}${id}${specVersion}?type=vegaspec`;

        return this.httpService.get(url).pipe(
            map(({ data }) => data),
            map(Validation.create.toThrow(VegaSpec.codec)),
            map(spec => resolveBootPlaceholders(spec, this.boot)),
        );
    }

    public getUtility(spec: AcSpecOrId): Observable<AcSpecification> {
        if (!isString(spec) && !View.specIdWithScopedProperties.is(spec)) return of(spec);
        if (!isString(spec) && spec.spec) return of(spec.spec);

        const id: string = isString(spec) ? spec : spec.name;
        const version: number | undefined = isString(spec) ? undefined : spec.version;
        const specVersion = version ? `/version/${version}` : '';
        const url = `${this.vegaSpecUrl}${id}${specVersion}?type=vegaspec`;

        // setting the context allows for inspecting portlet cache in search for utilities
        return this.httpService.get(url, { context: { utility: true } }).pipe(
            map(({ data }) => data),
            map(Validation.create.toThrow(VegaSpec.codec)),
            map(spec => resolveBootPlaceholders(spec, this.boot)),
        );
    }

    public static renderAclibSpec(
        specDefinition: AcSpecOrId,
        spec: AcSpecification | AcPipeDocument,
        signals: Signal[],
        repeats: Repeat[],
    ): Specification {
        const mappings: Partial<Specification['options']> = View.specIdWithScopedProperties.is(
            specDefinition,
        )
            ? omitBy(
                  {
                      importMapping: specDefinition.importMapping,
                      exportMapping: specDefinition.exportMapping,
                  },
                  isUndefined,
              )
            : {};

        return {
            type: VegaSpecificationService.toAclibSpecType(spec),
            source: spec,
            options: {
                signals: VegaSpecificationService.getSignalsForSpec(specDefinition, signals),
                repeats,
                ...mappings,
            },
        };
    }

    public static getDocumentImportMapping(
        specDefinition: AcSpecOrId,
    ): DocApplyOptions['importMapping'] {
        return View.docIdWithScopedProperties.is(specDefinition)
            ? specDefinition.importMapping || {}
            : {};
    }

    public static getSignalsForSpec(specDefinition: AcSpecOrId, signals: Signal[]): Signal[] {
        if (!View.requiredSpecIdWithScopedProperties.is(specDefinition)) return signals;

        const scopedSignals = Object.entries(specDefinition.props).map(
            ([name, valueOrSignalReference]) => {
                if (!SignalReference.codec.is(valueOrSignalReference))
                    return { name, value: valueOrSignalReference };

                const referencedSignal = signals.find(
                    s => s.name === valueOrSignalReference.signal,
                );

                // use spread so that we preserve both `value` and `update` expressions (and other vega options as well)
                return { ...referencedSignal, name };
            },
        );

        return VegaSpecificationService.mergeSignals<Signal>(scopedSignals, signals);
    }

    private static toAclibSpecType(spec: AcSpecification): SpecType {
        const mapping: Record<AcSpecification['usermeta']['type'], SpecType> = {
            source: SpecType.source,
            transform: SpecType.transform,
            document: SpecType.document,
            utility: SpecType.utility,
        };

        return mapping[spec.usermeta.type];
    }
}
